import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { ClosingAwareModal } from '~/components/closing-aware-modal'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useState, useEffect, useMemo, useCallback, useReducer } from 'react'
import { TpToggleButtonGroup, TpTooltip } from '~/components'
import { NumberFieldOrSpinner } from '~/components/number-spinner-field'
import { useFarmChildren } from '~/crop/crop-modal/hooks/use-farm-children'
import { useTaskTypes } from '~/tasks/hooks/use-task-types'
import { useCreateNewTask } from './use-create-new-task'
import { generatePreviewTaskData } from './generate-preview-task-data'
import { useExistingTasksData } from './use-existing-tasks-data'
import { supabaseGetter } from '~/api/supabase-getter'
import { parseIntervalToDays } from '~/common/utils/date-utils'
import { DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'
import CropScheduleTimeline from '~/crop/crop-schedule/crop-timeline'
import { useOrgContext } from '~/common/context/organization/org-context'
import { useTask } from '../hooks/use-task'
import { initialTaskFormState, taskFormReducer } from './task-form-reducer'
import {
  ORGANIZATION_TYPE_GROUP_LABELS,
  SELECT_ENTITY_TOOLTIP,
  SELECT_TASK_TYPE_TOOLTIP,
  TARGET_TYPES,
  TIMELINE_PREVIEW_CONTAINER_HEIGHT,
  TIMELINE_PREVIEW_CONTENT_HEIGHT
} from './task-constants'

/**
 * Modal for creating a custom task.
 *
 * @param {boolean} open
 * @param {function} onClose
 */
export const TaskModal = ({ taskId, open, onClose, farmId }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const { task } = useTask({ taskId })

  const [crops, setCrops] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [form, dispatch] = useReducer(taskFormReducer, { ...initialTaskFormState, startDate: dayjs() })
  const { targetType, selectedEntity, taskCode, startDate, durationDays, notes } = form

  // Data hooks
  const { activeOrgs: [ farmOrg ] } = useOrgContext()
  const { childOrgs, invalidate: invalidateOrgs } = useFarmChildren({ type: 'plot', farmId })
  const allOrgs = useMemo(() => [farmOrg, ...childOrgs], [farmOrg, childOrgs])
  const { orgTaskTypes, cropTaskTypes } = useTaskTypes()

  // Fetch all crops in the farm whenever plots are ready
  useEffect(() => {
    const orgIds = allOrgs.filter(Boolean).map(p => p.id)
    if (orgIds.length === 0) {
      setCrops([])
      return
    }
    setCrops([])
    supabaseGetter.crops.getByOrganizations(orgIds).then(({ data, error }) => {
      if (error) console.error('[TaskModal] crops fetch error:', error)
      setCrops(data || [])
    })
  }, [allOrgs])


  useEffect(() => {
    if (open) invalidateOrgs()
  }, [open, invalidateOrgs])

  useEffect(() => {
    if (!task) return

    dispatch({
      type: 'INIT_FROM_TASK',
      payload: {
        targetType: task.crop_id ? 'crop' : 'org',
        selectedEntity: task,
        taskCode: task.task_code,
        startDate: task.task_window_start,
        durationDays: task.duration,
        notes: task.notes,
      },
    })
  }, [task])

  // Filtered task types based on target type selection
  const availableTaskTypes = useMemo(
    () => targetType === 'org' ? orgTaskTypes : targetType === 'crop' ? cropTaskTypes : [],
    [targetType, orgTaskTypes, cropTaskTypes]
  )

  // Entity options for the second selector
  const entityOptions = useMemo(() => {
    if (targetType === 'org') return allOrgs
    if (targetType === 'crop') return crops
    return []
  }, [targetType, allOrgs, crops])

  // Resolve org/crop IDs for the task hook
  const orgId = useMemo(() => {
    if (!selectedEntity) return null
    if (targetType === 'org') return selectedEntity.id
    return selectedEntity.organization_id ?? null
  }, [targetType, selectedEntity])

  const cropId = targetType === 'crop' ? selectedEntity?.id ?? null : null

  // Resolve human-readable task type name for the timeline label
  const taskTypeName = useMemo(
    () => availableTaskTypes.find(t => t.code === taskCode)?.name ?? taskCode,
    [availableTaskTypes, taskCode]
  )

  // Existing tasks for the selected entity — shown as read-only context in the timeline
  const { existingTasksData } = useExistingTasksData({ targetType, selectedEntity })

  const { submitTask, loading } = useCreateNewTask({
    taskCode,
    orgId,
    cropId,
    startDate,
    durationDays,
    notes,
  })

  // Preview timeline data — updates reactively as form state changes
  const previewScheduleData = useMemo(() => {
    if (!taskCode || !startDate) return []
    return generatePreviewTaskData({
      taskCode,
      taskTypeName,
      startDate,
      durationDays,
      notes,
      orgId,
      cropId,
    })
  }, [taskCode, taskTypeName, startDate, durationDays, notes, orgId, cropId])

  // Merge preview + existing tasks into ONE row so they share the same timeline row.
  // Preview task is placed first so tidx=0 aligns with drag/pan handler indices.
  const combinedScheduleData = useMemo(() => {
    if (!selectedEntity) return null

    const previewRow = previewScheduleData[0]
    const existingRow = existingTasksData[0]

    // Return empty override (prevents farm data from loading) when no data yet
    if (!previewRow && !existingRow) return []

    const baseRow = previewRow || existingRow
    const allTasks = [
      ...(previewRow?.tasks || []),
      ...(existingRow?.tasks || []),
    ]

    return [{ ...baseRow, tasks: allTasks }]
  }, [selectedEntity, previewScheduleData, existingTasksData])

  // Scroll timeline to task start date
  const timelineScrollConfig = useMemo(() => {
    if (!previewScheduleData?.length) return { scrollToDate: null, scrollToLeftOffset: undefined }
    return {
      scrollToDate: startDate?.toDate() ?? null,
      scrollToLeftOffset: 150,
    }
  }, [previewScheduleData, startDate])

  // Sync form state from timeline drag adjustments
  const handleTaskAdjust = useCallback((updatedTasks) => {
    const updated = updatedTasks?.[0]
    if (!updated) return
    if (updated.task_window_start) {
      dispatch({ type: 'SET', key: 'startDate', value: dayjs(updated.task_window_start) })
    }
    if (updated.duration) {
      dispatch({ type: 'SET', key: 'durationDays', value: parseIntervalToDays(updated.duration) })
    }
  }, [])

  const isValid = Boolean(targetType && selectedEntity && taskCode && startDate)

  const _onClose = useCallback(() => {
    dispatch({ type: 'RESET' })
    onClose?.()
  }, [onClose])

  const onSubmit = useCallback(async () => {
    setIsSubmitting(true)
    try {
      await submitTask()
    } finally {
      setIsSubmitting(false)
    }
    _onClose()
  }, [submitTask, _onClose])

  // Reset entity + task code when target type changes
  const handleTargetTypeChange = useCallback((_, newValue) => {
    if (!newValue) return
    dispatch({ type: 'INIT_FROM_TASK', payload: { ...initialTaskFormState, startDate: dayjs(), targetType: newValue } })
  }, [])

  // Reset task code when entity changes
  const handleEntityChange = useCallback((_, newValue) => {
    dispatch({ type: 'SET', key: 'selectedEntity', value: newValue })
    dispatch({ type: 'SET', key: 'taskCode', value: null })
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ClosingAwareModal
        open={open}
        onClose={_onClose}
        loading={isSubmitting}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
          py: 4,
        }}
      >
        <Box
          component="form"
          sx={{
            position: 'relative',
            width: { xs: '90vw', md: '80vw', lg: '60vw' },
            bgcolor: 'background.paper',
            borderRadius: '0.5rem',
            boxShadow: 24,
            p: '1rem 2rem',
            my: 'auto',
          }}
        >
          <Typography variant="h4">Add Task</Typography>
          <Divider sx={{ pb: '0.5rem' }} variant="fullWidth" />

          {/* Row 1: Target type toggle + entity selector */}
          {!taskId && (
            <Stack
              spacing="1rem"
              sx={{ pt: '1rem', alignItems: 'center' }}
            >
              <TpToggleButtonGroup
                value={targetType}
                exclusive
                onChange={handleTargetTypeChange}
              >
                {TARGET_TYPES.map(({ code, label }) => (
                  <ToggleButton key={code} value={code}>
                    {label}
                  </ToggleButton>
                ))}
              </TpToggleButtonGroup>

              <Box sx={{ width: '100%' }}>
                <TpTooltip
                  followCursor
                  placement="top"
                  title={!targetType ? SELECT_ENTITY_TOOLTIP : ''}
                >
                  <Autocomplete
                    size={isDesktop ? 'medium' : 'small'}
                    disabled={!targetType}
                    options={entityOptions}
                    value={selectedEntity}
                    onChange={handleEntityChange}
                    groupBy={
                      targetType === 'org'
                        ? (option) => ORGANIZATION_TYPE_GROUP_LABELS[option.organization_type]
                        : undefined
                    }
                    getOptionLabel={(option) => option.name || option.crop_templates?.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{option.name || option.crop_templates?.name}</Typography>
                          {targetType === 'crop' && option.organization?.name && (
                            <Typography variant="caption" color="text.secondary">
                              {option.organization.name}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          targetType === 'org'
                            ? 'Select Organization'
                            : targetType === 'crop'
                            ? 'Select Crop'
                            : 'Select...'
                        }
                      />
                    )}
                  />
                </TpTooltip>
              </Box>
            </Stack>
          )}

          {/* Row 2: Task type + date/duration (left 4) + notes (right 8) */}
          <Grid
            container
            spacing="1rem"
            rowSpacing="0.5rem"
            sx={{ pt: '1rem' }}
          >
            {/* Left column: task type selector + date + duration */}
            <Grid size={{ xs: 12, lg: 5, xl: 4 }}>
              <Grid container rowSpacing="0.75rem">

                {/* Task type selector — full width in left column */}
                <Grid size={12}>
                  <TpTooltip
                    followCursor
                    placement="top"
                    title={!selectedEntity ? SELECT_TASK_TYPE_TOOLTIP : ''}
                  >
                    <TextField
                      select
                      fullWidth
                      size={isDesktop ? 'medium' : 'small'}
                      label="Task Type"
                      value={taskCode ?? ''}
                      disabled={!selectedEntity}
                      onChange={(e) => dispatch({ type: 'SET', key: 'taskCode', value: e.target.value || null })}
                    >
                      {availableTaskTypes.length === 0 ? (
                        <MenuItem value="" disabled>
                          {selectedEntity ? 'No task types available' : 'Select a target first'}
                        </MenuItem>
                      ) : (
                        availableTaskTypes.map((tt) => (
                          <MenuItem key={tt.code} value={tt.code}>
                            {tt.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </TpTooltip>
                </Grid>

                {/* Start date + duration side by side */}
                <Grid size={12}>
                  <Grid container spacing="0.5rem" alignItems="flex-end">
                    <Grid size={7}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newDate) => dispatch({ type: 'SET', key: 'startDate', value: newDate })}
                        disabled={!taskCode}
                        slotProps={{
                          textField: {
                            size: isDesktop ? 'medium' : 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={5}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          '& .MuiButton-root': { height: { xs: '40px' } },
                        }}
                      >
                        <NumberFieldOrSpinner
                          isDesktop={isDesktop}
                          showLabel
                          label="Duration (days)"
                          min={1}
                          value={durationDays}
                          onValueChange={(val) => dispatch({ type: 'SET', key: 'durationDays', value: val })}
                          disabled={!taskCode}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Right column: notes */}
            <Grid size={{ xs: 12, lg: 7, xl: 8 }}>
              <TextField
                label="Notes"
                multiline
                minRows={4}
                maxRows={6}
                variant="filled"
                fullWidth
                value={notes}
                slotProps={{ htmlInput: { style: { resize: 'none' } } }}
                onChange={(e) => dispatch({ type: 'SET', key: 'notes', value: e.target.value || '' })}
                sx={{
                  height: '100%',
                  '& .MuiFilledInput-root': { height: '100%', alignItems: 'flex-start' },
                }}
              />
            </Grid>
          </Grid>

          {/* Row 3: Timeline — visible once an entity is selected; shows existing tasks + preview */}
          <Box sx={{ pt: '1rem' }}>
            <Typography variant="subtitle1" sx={{ pb: '0.25rem' }}>
              {!taskId ? 'Adjust New Task' : 'Edit Existing Tasks'}
            </Typography>
            <Box
              sx={{
                height: TIMELINE_PREVIEW_CONTAINER_HEIGHT,
                '@media screen and (-webkit-min-device-pixel-ratio:0) and (min-resolution: .001dpcm)': {
                  height: TIMELINE_PREVIEW_CONTAINER_HEIGHT,
                },
                '@supports (-webkit-touch-callout: none)': {
                  height: TIMELINE_PREVIEW_CONTAINER_HEIGHT,
                },
              }}
            >
              <CropScheduleTimeline
                scheduleData={selectedEntity ? combinedScheduleData : []}
                hideYAxis
                hidePhaseTooltip
                hideRowLines
                hideToggleButtons
                forceAllExpand
                contentHeight={TIMELINE_PREVIEW_CONTENT_HEIGHT}
                interactive
                dayPrecision
                viewStrategy="tasks"
                onTaskAdjust={handleTaskAdjust}
                scrollToDate={timelineScrollConfig.scrollToDate}
                scrollToLeftOffset={timelineScrollConfig.scrollToLeftOffset}
              />
            </Box>
          </Box>

          {/* Action buttons */}
          <Box
            sx={{
              mt: '1rem',
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
            }}
          >
            <Button variant="outlined" size="large" onClick={_onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="large"
              disabled={!isValid || loading}
              onClick={onSubmit}
            >
              Add Task
            </Button>
          </Box>
        </Box>
      </ClosingAwareModal>
    </LocalizationProvider>
  )
}
